// @intent: Native match-3 runtime view-model assembly, before Pixi object mounting.
// @flow: Match3LevelV1 + Match3SkinPackV1 -> layout + sprite placements + probe facts.
// @stale: This is renderer-facing data only; gameplay state transitions remain in @mintyads/playable-api.

import type { Match3LevelV1, Match3ProbeStateV1, Match3SkinPackV1 } from '@mintyads/playable-api';
import { computeMatch3CellRects, computeMatch3Layout, type Match3LayoutResult, type Match3Viewport } from './Match3Layout.js';
import { createMatch3SpritePlacement, type Match3SpritePlacement } from './Match3Sprites.js';

export interface Match3RuntimeViewModel {
  readonly layout: Match3LayoutResult;
  readonly tileSprites: readonly Match3SpritePlacement[];
  readonly staticSprites: readonly Match3SpritePlacement[];
  readonly probe: Pick<Match3ProbeStateV1, 'rows' | 'cols' | 'cellRects' | 'tileLayerIds' | 'staticCellIndexes'>;
}

export interface CreateMatch3RuntimeViewModelInput {
  readonly level: Match3LevelV1;
  readonly skin: Match3SkinPackV1;
  readonly viewport: Match3Viewport;
}

function indexAt(row: number, col: number, cols: number): number {
  return row * cols + col;
}

function tileAsset(level: Match3LevelV1, tileId: string): string {
  return level.tiles.find((tile) => tile.id === tileId)?.assetUrl ?? '';
}

function staticAsset(skin: Match3SkinPackV1, stage: number | undefined): string {
  const index = Math.max(0, Math.min(skin.slots.objectiveStages.length - 1, Math.trunc(stage ?? 0)));
  return skin.slots.objectiveStages[index] ?? skin.slots.objectiveStages[0] ?? '';
}

export function createMatch3RuntimeViewModel(input: CreateMatch3RuntimeViewModelInput): Match3RuntimeViewModel {
  const rows = input.level.board.rows;
  const cols = input.level.board.cols;
  const layout = computeMatch3Layout({
    viewport: input.viewport,
    rows,
    cols,
    tileCellFill: input.skin.fit.tileCellFill,
    boardSizing: input.level.layout.sizing,
  });
  const cells = computeMatch3CellRects(layout.board, rows, cols, input.skin.fit.tileCellFill);
  const staticByIndex = new Map<number, NonNullable<Match3LevelV1['board']['staticCells']>[number]>();
  for (const staticCell of input.level.board.staticCells ?? []) {
    staticByIndex.set(indexAt(staticCell.at.row, staticCell.at.col, cols), staticCell);
  }

  const tileSprites: Match3SpritePlacement[] = [];
  const staticSprites: Match3SpritePlacement[] = [];

  for (const cell of cells) {
    const staticCell = staticByIndex.get(cell.tileIndex);
    if (staticCell) {
      staticSprites.push(createMatch3SpritePlacement({
        id: `match-static-${String(cell.tileIndex)}`,
        cell,
        skin: input.skin,
        assetUrl: staticAsset(input.skin, staticCell.stage),
        swappable: false,
      }));
      continue;
    }

    const tileId = input.level.board.cells[cell.tileIndex];
    if (!tileId) continue;
    tileSprites.push(createMatch3SpritePlacement({
      id: `match-tile-${String(cell.tileIndex)}`,
      cell,
      skin: input.skin,
      assetUrl: tileAsset(input.level, tileId),
      swappable: true,
    }));
  }

  return {
    layout,
    tileSprites,
    staticSprites,
    probe: {
      rows,
      cols,
      cellRects: cells.map((cell) => ({
        tileIndex: cell.tileIndex,
        row: cell.row,
        col: cell.col,
        x: cell.x,
        y: cell.y,
        width: cell.width,
        height: cell.height,
      })),
      tileLayerIds: tileSprites.map((sprite) => sprite.id),
      staticCellIndexes: [...staticByIndex.keys()].sort((a, b) => a - b),
    },
  };
}
